import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, Injectable, provideAppInitializer, provideExperimentalZonelessChangeDetection, TransferState } from '@angular/core';
import { provideRouter, Router, RouterStateSnapshot, TitleStrategy, withInMemoryScrolling, withRouterConfig } from '@angular/router';
import { DecimalPipe, IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { HAMMER_GESTURE_CONFIG, HAMMER_LOADER, HammerGestureConfig, HammerModule, provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { LIGHTBOX_CONFIG, LightboxConfig, LightboxModule } from 'ng-gallery/lightbox';
import * as Sentry from '@sentry/angular';
import { TraceService } from '@sentry/angular';

import { routes } from './app.routes';
import { Globals } from '@core/globals';
import { RoutingData } from '@core/routing-data';
import { LanguageService } from '@services/language.service';
import { SeoService } from '@services/seo.service';
import { Language } from '@models/language.model';
import { TranslateBrowserLoader } from '@loaders/translate-browser.loader';
import { ThousandsPipe } from '@pipes/thousands.pipe';
import { PricePipe } from '@pipes/price.pipe';
import { environment } from '../environments/environment';

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  override overrides = <any>{
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

@Injectable()
export class TemplatePageTitleStrategy extends TitleStrategy {
  private languageService = inject(LanguageService);
  private seoService = inject(SeoService);

  override updateTitle(routerState: RouterStateSnapshot) {
    const title: string | undefined = this.buildTitle(routerState);
    const language: Language = this.languageService.language();

    if (title !== undefined && title !== 'custom') {
      this.seoService.setTitle(title);
      this.seoService.setOgData({ title: title });
    } else if (title !== 'custom') {
      this.seoService.setTitle(RoutingData.defaultTitle[language]);
      this.seoService.setOgData({ title: RoutingData.defaultTitle[language] });
    }
    this.seoService.setDescription();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    provideRouter(
      routes,
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        includePostRequests: false,
      }),
    ),
    provideHttpClient(withFetch()),
    importProvidersFrom([BrowserAnimationsModule]),
    provideHotToastConfig({
      position: 'top-right',
      duration: 5000,
      dismissible: true,
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: TranslateBrowserLoader,
          deps: [HttpClient, TransferState, Globals],
        },
      }),
    ),
    { provide: MAT_DATE_LOCALE, useValue: 'cs-CZ' },
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        if (config.width && (config.src.includes('https://aaaautoczimg.vshcdn.net/new/') || config.src.includes('https://aaaautoeuimg.vshcdn.net/thumb/') || config.src.includes('https://img.aaaauto.cz/new/') || config.src.includes('https://img.mototechna.cz/strapi/') || config.src.includes('https://imgmototechnacz.vshcdn.net/strapi/'))) {
          const suffix: string = config.src.split('.').pop() || '';
          if (suffix === 'svg') return config.src;
          const urlSplit = config.src.split('_');
          const sizes = urlSplit[urlSplit.length - 1];
          const width: string = sizes.split('x')[0] || '0';
          const height: string = sizes.split('x')[1] || '0';
          const aspectRatio = Number(height) / Number(width);
          const newWidth = config.width ? config.width.toString() : width;
          const newHeight = Math.ceil(Number(newWidth) * aspectRatio).toString();
          const newSizes = sizes.replace(width, newWidth).replace(height.toString(), newHeight);
          config.src = config.src.replace(sizes, newSizes);
        }

        return config.src;
      },
    },
    importProvidersFrom(LightboxModule),
    {
      provide: LIGHTBOX_CONFIG,
      useValue: {
        exitAnimationTime: 500,
        panelClass: 'fullscreen',
      } as LightboxConfig,
    },
    { provide: HAMMER_LOADER, useValue: () => import('hammerjs') },
    importProvidersFrom(HammerModule),
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    ...(environment.production ? [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler({
          showDialog: false,
        }),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(TraceService);
      }),
    ] : []),
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
    },
    DecimalPipe,
    ThousandsPipe,
    PricePipe,
  ],
};
